import { useEffect, useState } from 'react';
import { fetchMarketRenewalRisk } from '@/lib/marketExperienceApi';
import { matchRenewalRiskRow, renewalRiskRowsFromPayload } from '@/lib/accountRenewalRisk';

const TIMEOUT_MS = 2000;
const cache = new Map();
const inflight = new Map();

function cacheKey(tenantId, lens) {
  return `${tenantId || '_'}::${lens || ''}`;
}

function fetchList(tenantId, lens) {
  const key = cacheKey(tenantId, lens);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);

  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error('timeout')), TIMEOUT_MS);
  });

  const promise = Promise.race([
    fetchMarketRenewalRisk(tenantId, lens),
    timeoutPromise,
  ])
    .then((payload) => {
      const rows = renewalRiskRowsFromPayload(payload);
      cache.set(key, rows);
      return rows;
    })
    .finally(() => {
      window.clearTimeout(timeoutId);
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

/** Tenant-scoped renewal-risk list. Fail-open: errors and timeouts yield no rows. */
export function useRenewalRisk(tenantId, lens) {
  const key = cacheKey(tenantId, lens);
  const [rows, setRows] = useState(() => cache.get(key) || []);

  useEffect(() => {
    if (!tenantId || !lens) {
      setRows([]);
      return undefined;
    }
    const hit = cache.get(key);
    if (hit) {
      setRows(hit);
      return undefined;
    }
    setRows([]);
    let cancelled = false;
    fetchList(tenantId, lens)
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, lens, key]);

  return rows;
}

export function useAccountRenewalRisk(tenantId, lens, account) {
  const rows = useRenewalRisk(tenantId, lens);
  return matchRenewalRiskRow(rows, account);
}
